/**
 * HTTP client for the Worker admin LLM surface — issue #119 (wires the
 * client view shipped in #92's scaffolding).
 *
 * Every call:
 *   - is cookie-credentialed (the session cookie is the load-bearing
 *     auth token; the worker's `requireSystemAdmin` middleware reads it
 *     and returns **404** for non-admins so the route's existence is
 *     not leaked).
 *   - resolves to a discriminated union `AdminResult<T>` so the view
 *     can branch on the kinds it cares about: `not-found` (the view
 *     shows the "Not found" panel), `unauthenticated` (deep-link prompt
 *     to sign in), or `error` (inline retry).
 *   - never echoes plaintext API keys back from any GET response — the
 *     worker enforces this on the inbound surface (see
 *     `apps/worker/src/admin/llm.ts` "Plaintext discipline" comment).
 */
import { createAdminRequest, type AdminResult } from './admin-fetch.js';

export type { AdminResult } from './admin-fetch.js';

// The 404 mapping covers both the admin gate and inner "not found" (e.g. a
// provider id that doesn't exist); the view distinguishes them by which
// fetch was in flight.
const request = createAdminRequest('/v1/admin/llm');

// ─────────────────────────────────────────────────────────── providers ─

export interface Provider {
  id: string;
  adapter_key: string;
  display_name: string;
  enabled: boolean;
  /** Local / OpenAI-compatible endpoint URL, or null for the adapter default. */
  base_url: string | null;
  models_count: number;
  active_keys_count: number;
  adapter_registered: boolean;
  created_at: string;
  updated_at: string;
}

export function listProviders(): Promise<AdminResult<{ providers: Provider[] }>> {
  return request('/providers');
}

export function patchProvider(
  id: string,
  body: { enabled?: boolean; display_name?: string; base_url?: string | null },
): Promise<AdminResult<{ ok: true }>> {
  return request(`/providers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function testProviderConnection(
  id: string,
): Promise<AdminResult<{ ok: boolean; error?: string }>> {
  return request(`/providers/${encodeURIComponent(id)}/test`, {
    method: 'POST',
    body: '{}',
  });
}

// ─────────────────────────────────────────────────────────── models ─

/** Capability flags the catalog/pricing overlay carries (#158/#165). */
export interface ModelCapabilities {
  function_calling: boolean;
  image_ref: boolean;
  negative_prompt: boolean;
  vision: boolean;
}

/** The `llm_model_pricing` overlay for a model (null until priced). */
export interface ModelPricing {
  modality: 'text' | 'image' | 'embedding';
  cost_input_per_1m_micro: number | null;
  cost_output_per_1m_micro: number | null;
  cost_image_per_tile_step_in_micro: number | null;
  cost_image_per_tile_step_out_micro: number | null;
  capabilities: ModelCapabilities;
}

export interface Model {
  id: string;
  provider_id: string;
  model_key: string;
  display_name: string;
  dim: number | null;
  deprecated: boolean;
  deprecated_at: string | null;
  cost_input_per_1m: number;
  cost_output_per_1m: number;
  created_at: string;
  /** Pricing overlay (#158/#165) — null until priced. */
  pricing: ModelPricing | null;
  /** Bindable when priced on every dimension its modality bills on. */
  bindable: boolean;
}

export function listModels(providerId: string): Promise<AdminResult<{ models: Model[] }>> {
  return request(`/providers/${encodeURIComponent(providerId)}/models`);
}

/** Summary of a catalog sync (#158) — what the live Cloudflare sync changed. */
export interface CatalogSyncSummary {
  total: number;
  inserted: number;
  updated: number;
  deprecated: number;
  priced: number;
}

/** Sync the Cloudflare model catalog into `llm_models` (auto-prices what it can). */
export function syncCatalog(): Promise<AdminResult<{ ok: true; summary: CatalogSyncSummary }>> {
  return request('/catalog/sync', { method: 'POST' });
}

/** Edit a model's pricing overlay + capability flags (#158/#165). */
export function setModelPricing(body: {
  model_key: string;
  modality: 'text' | 'image' | 'embedding';
  cost_input_per_1m_micro?: number | null;
  cost_output_per_1m_micro?: number | null;
  cost_image_per_tile_step_in_micro?: number | null;
  cost_image_per_tile_step_out_micro?: number | null;
  capability_flags?: string[];
}): Promise<AdminResult<{ ok: true; bindable: boolean }>> {
  return request('/models/pricing', { method: 'PUT', body: JSON.stringify(body) });
}

export function createModel(
  providerId: string,
  body: {
    model_key: string;
    display_name?: string;
    dim?: number;
    cost_input_per_1m?: number;
    cost_output_per_1m?: number;
  },
): Promise<AdminResult<{ id: string; model_key: string; display_name: string }>> {
  return request(`/providers/${encodeURIComponent(providerId)}/models`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ─────────────────────────────────────────────────────────── keys ─

export interface KeySummary {
  id: string;
  provider_id: string;
  last_four: string;
  label: string;
  created_by_user_id: string;
  created_at: string;
  last_rotated_at: string | null;
  retired_at: string | null;
}

export interface AddKeyResponse {
  id: string;
  provider_id: string;
  last_four: string;
  label: string;
  created_at: string;
}

export function listKeys(providerId: string): Promise<AdminResult<{ keys: KeySummary[] }>> {
  return request(`/providers/${encodeURIComponent(providerId)}/keys`);
}

export function addKey(
  providerId: string,
  body: { plaintext_key: string; label: string },
): Promise<AdminResult<AddKeyResponse>> {
  return request(`/providers/${encodeURIComponent(providerId)}/keys`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function rotateKey(
  keyId: string,
  body: { plaintext_key: string; label?: string },
): Promise<AdminResult<AddKeyResponse & { retired_id: string; grace_window_hours: number }>> {
  return request(`/keys/${encodeURIComponent(keyId)}/rotate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function retireKey(keyId: string): Promise<AdminResult<{ ok: true }>> {
  return request(`/keys/${encodeURIComponent(keyId)}/retire`, {
    method: 'POST',
    body: '{}',
  });
}

// ─────────────────────────────────────────────────────────── bindings ─

/** Per-binding generation knobs (reasoning_effort for text; size/steps for images). */
export interface BindingConfig {
  reasoning_effort?: 'none' | 'low' | 'medium' | 'high';
  size?: string;
  steps?: number;
}

export interface Binding {
  id: string;
  function_key: string;
  posture: string;
  provider_id: string;
  model_id: string;
  /** JSON of the generation knobs, or null. Parse with `parseBindingConfig`. */
  config: string | null;
  provider_adapter_key: string;
  provider_display_name: string;
  model_key: string;
  model_display_name: string;
  created_at: string;
  updated_at: string;
}

/** Parse a binding's `config` JSON column into a typed object (never throws). */
export function parseBindingConfig(raw: string | null | undefined): BindingConfig {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as BindingConfig)
      : {};
  } catch {
    return {};
  }
}

export function listBindings(): Promise<AdminResult<{ bindings: Binding[] }>> {
  return request('/bindings');
}

export function setBinding(body: {
  function_key: string;
  posture: string;
  provider_id: string;
  model_id: string;
  /** Omit to leave existing config untouched; pass an object to replace it. */
  config?: BindingConfig;
}): Promise<AdminResult<{ ok: true }>> {
  return request('/bindings', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// ─────────────────────────────────────────────────── prompt templates ─

export interface PromptTemplate {
  key: string;
  body: string;
  is_override: boolean;
  placeholders: string[];
  /** Placeholders the body must keep (the editor warns if one is missing). */
  required: string[];
}

export function listTemplates(): Promise<AdminResult<{ templates: PromptTemplate[] }>> {
  return request('/templates');
}

/** Set a template override, or reset to the code default with `{reset:true}`. */
export function setTemplate(body: {
  template_key: string;
  body?: string;
  reset?: boolean;
}): Promise<AdminResult<{ ok: true }>> {
  return request('/templates', { method: 'PUT', body: JSON.stringify(body) });
}

/** One append-only version row in a template's history (browse/diff/revert). */
export interface TemplateVersion {
  version: number;
  body: string;
  created_at: string;
  created_by: string | null;
  /** The live override points here. */
  is_current: boolean;
}

/** List a template's version history, most-recent-first, + the live pointer. */
export function listTemplateVersions(
  templateKey: string,
): Promise<AdminResult<{ versions: TemplateVersion[]; current_version: number | null }>> {
  return request(`/templates/${encodeURIComponent(templateKey)}/versions`);
}

/**
 * Revert a template to a historical version: appends a NEW version capturing
 * the revert + points current_version at it (append-only — history is never
 * mutated). `missing_required` mirrors the editor's non-blocking warning.
 */
export function revertTemplate(
  templateKey: string,
  version: number,
): Promise<AdminResult<{ ok: true; version: number; missing_required: string[] }>> {
  return request(`/templates/${encodeURIComponent(templateKey)}/revert`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
}

// ─────────────────────────────────────────────────────────── adapters ─

export interface AdapterInfo {
  id: string;
  display_name: string;
  supported_tasks: string[];
  models: { id: string; display_name: string; dim?: number | null }[];
}

export function listAdapters(): Promise<AdminResult<{ adapters: AdapterInfo[] }>> {
  return request('/adapters');
}


// ─────────────────────────────────────────────────────────── audit ─

export interface AuditEntry {
  id: string;
  admin_user_id: string;
  action: string;
  provider_id: string | null;
  key_id: string | null;
  metadata: Record<string, unknown> | null;
  at: string;
}

export function listAudit(limit = 100): Promise<AdminResult<{ entries: AuditEntry[] }>> {
  return request(`/audit?limit=${encodeURIComponent(String(limit))}`);
}

// ─────────────────────────────────────────────────────────── analytics ─

/** The trace-backed spend dashboard window (#165 increment 3). */
export type AnalyticsWindow = '30d' | '7d' | 'all';

export interface SpendByFunction {
  function_key: string;
  trace_count: number;
  cost_micro_usd: number;
}

export interface SpendByModel {
  model_key: string;
  call_count: number;
  input_tokens: number;
  output_tokens: number;
  cost_micro_usd: number;
}

export interface SpendByCampaign {
  campaign_id: string;
  trace_count: number;
  cost_micro_usd: number;
}

export interface UsageByPromptVersion {
  function_key: string;
  prompt_template_key: string;
  prompt_template_version: number;
  span_count: number;
  cost_micro_usd: number;
}

export interface ToolCallFrequency {
  tool_name: string;
  call_count: number;
}

export interface LatencyStats {
  sample_count: number;
  avg_ms: number | null;
  p50_ms: number | null;
}

export interface Analytics {
  window: AnalyticsWindow;
  since: string | null;
  total_cost_micro_usd: number;
  total_trace_count: number;
  by_function: SpendByFunction[];
  by_model: SpendByModel[];
  by_campaign: SpendByCampaign[];
  by_prompt_version: UsageByPromptVersion[];
  tool_calls: ToolCallFrequency[];
  latency: LatencyStats;
  /** Whether draft outcomes are linked to a trace/prompt version (currently false). */
  acceptance_tracked: boolean;
}

/** Fetch the trace-backed analytics dashboard for a time window. */
export function getAnalytics(window: AnalyticsWindow): Promise<AdminResult<Analytics>> {
  return request(`/analytics?window=${encodeURIComponent(window)}`);
}

// ─────────────────────────── cost-control policy (#216/#217/#218) ─

/**
 * The deployment-wide LLM cost-control policy (the `llm_global_settings`
 * singleton): the per-Account monthly budget cap + whether "unlimited" may be
 * enabled, the soft-warning thresholds, the multi-window rate limits, and the
 * Account-wide kill switch. Every field is runtime-tunable here — no deploy
 * constant (#216).
 */
export interface LlmSettings {
  budget_micro_usd: number;
  unlimited_allowed: boolean;
  warn_pct: [number, number, number];
  rate_per_minute: number;
  rate_per_hour: number;
  rate_per_day: number;
  kill_switch: boolean;
}

export function getLlmSettings(): Promise<AdminResult<LlmSettings>> {
  return request('/settings');
}

/** Patch any subset of the policy; the worker validates + merges + audits. */
export function putLlmSettings(body: Partial<LlmSettings>): Promise<AdminResult<LlmSettings>> {
  return request('/settings', { method: 'PUT', body: JSON.stringify(body) });
}

/** One-click Account-wide kill switch (#218). */
export function setKillSwitch(engaged: boolean): Promise<AdminResult<LlmSettings>> {
  return request('/kill-switch', { method: 'POST', body: JSON.stringify({ engaged }) });
}
